// PLAT-10268 (T8) — the Translation Review workspace's transient coverage cache.
//
// The AI coverage verdict for an edited span is workspace-local state (D6: it
// never lives in `TranslationStore`). It is keyed by the span's LEAD stable id —
// the UUID (`originalElementIds[0]`, source order) of the first source word it
// covers — the same id the translated column stamps as `data-source-uuid` (so
// the badge can look it up). Stable ids, NOT positional `initial_index`, so the
// verdict survives reorder / re-segmentation / montage.
//
// A phrase with no stable ids is a stale, pre-PLAT-10611 translation; it never
// overlaps here and so is never checked, cached, or shown — coverage is only
// meaningful once such a translation is regenerated with stable ids.
//
// A cached verdict is only valid while the span's translation is unchanged
// (AC 5: the score clears when the segment's translation changes). Rather than
// hooking every edit path, each entry stores a `signature` — the joined
// translated text of the covering phrases when the verdict was computed — and
// `verdictForLead` re-derives that signature at read time. Because the workspace
// is a MobX `observer`, any edit (re-edit, Undo, a fresh override) that changes
// the covering `translatedValue` re-renders it, the signature no longer matches,
// and the stale verdict is never shown.

import type {
  CoverageVerdict,
  TranslationPhrase,
} from '@src/types.ts/translation';

export interface CachedCoverage {
  verdict: CoverageVerdict;
  /** The stable source-word ids the verdict was computed for (source order; `[0]` is the lead). */
  originalElementIds: string[];
  /** Joined translated text of the covering phrases at compute time (the freshness fingerprint). */
  signature: string;
}

export type CoverageCache = Map<string, CachedCoverage>;

/** The lead stable id (first covered word, source order) a span's verdict is cached under. */
export function coverageLead(originalElementIds: readonly string[]): string {
  return originalElementIds[0];
}

/**
 * The freshness fingerprint for a span: the `translatedValue` of the ACTIVE
 * phrases covering `originalElementIds`. Retired (D10 `active: false`) fragments
 * are excluded — they are not rendered and would poison the signature. A phrase
 * with no stable ids (a stale translation) never overlaps, so it is never
 * fingerprinted. Serialized as an array so phrase boundaries are preserved
 * (`["a","bc"]` stays distinct from `["ab","c"]`).
 */
export function coverageSignature(
  phrases: readonly TranslationPhrase[],
  originalElementIds: readonly string[],
): string {
  const covered = new Set(originalElementIds);
  return JSON.stringify(
    phrases
      .filter(
        p =>
          p.active !== false &&
          (p.originalElementIds?.some(id => covered.has(id)) ?? false),
      )
      .map(p => p.translatedValue),
  );
}

/**
 * The cached verdict for `leadUuid`, or `undefined` when there is none OR the
 * span's translation has changed since it was computed (stale → never shown, AC 5).
 */
export function verdictForLead(
  cache: CoverageCache,
  leadUuid: string,
  phrases: readonly TranslationPhrase[],
): CoverageVerdict | undefined {
  const entry = cache.get(leadUuid);
  if (!entry) return undefined;
  return coverageSignature(phrases, entry.originalElementIds) ===
    entry.signature
    ? entry.verdict
    : undefined;
}

/**
 * The cached verdict for the fragment covering `originalElementIds` (PLAT-10268
 * BUG B). Same freshness check as {@link verdictForLead}, but resolves the entry
 * by STABLE-ID overlap instead of exact lead equality.
 *
 * Why overlap, not equality: an entry is keyed by the lead id of the span the
 * reviewer edited. On a re-fix of an existing override the store re-derives the
 * WHOLE segment's fragments, so the rendered fragment's lead id no longer equals
 * the entry's key (the re-selected sub-span's lead). The re-selected ids are
 * always a subset of the re-tiled fragment's ids, so id overlap finds the entry
 * where lead equality misses — the indicator survives the re-split. Stable ids
 * are partitioned across active fragments, so an entry overlaps exactly the
 * fragment(s) actually covering that span (no cross-fragment bleed).
 */
export function verdictForPhrase(
  cache: CoverageCache,
  originalElementIds: readonly string[],
  phrases: readonly TranslationPhrase[],
): CoverageVerdict | undefined {
  return entryForPhrase(cache, originalElementIds, phrases)?.verdict;
}

/**
 * The FRESH cached entry for the fragment covering `originalElementIds`, resolved
 * by stable-id overlap + the same signature freshness check (PLAT-10268 BUG B).
 * Returns the whole entry (not just the verdict) so the coverage-details popover
 * can read the covered span's ids. `undefined` when no fresh entry overlaps.
 */
export function entryForPhrase(
  cache: CoverageCache,
  originalElementIds: readonly string[],
  phrases: readonly TranslationPhrase[],
): CachedCoverage | undefined {
  const covered = new Set(originalElementIds);
  for (const entry of cache.values()) {
    if (!entry.originalElementIds.some(id => covered.has(id))) continue;
    if (
      coverageSignature(phrases, entry.originalElementIds) === entry.signature
    ) {
      return entry;
    }
  }
  return undefined;
}

/**
 * Whether the fragment covering `originalElementIds` has a coverage check IN
 * FLIGHT (PLAT-10268 BUG B). The loading set holds each in-flight span's lead id;
 * a fragment is loading when it CONTAINS one of those leads. Like
 * {@link verdictForPhrase} this resolves by stable id rather than exact lead
 * equality, so a spinner shown for a re-selected sub-span still lands on the
 * (re-tiled, wider) fragment that ends up rendering it. Because the loading lead
 * is a stable id partitioned across active fragments, each in-flight lead lands
 * on exactly one fragment.
 */
export function isCoverageLoadingForPhrase(
  loadingCoverageLeads: ReadonlySet<string> | undefined,
  originalElementIds: readonly string[],
): boolean {
  if (!loadingCoverageLeads || loadingCoverageLeads.size === 0) return false;
  return originalElementIds.some(id => loadingCoverageLeads.has(id));
}

/**
 * Whether `phrase` is the LAST phrase in `renderedPhrases` (the translated
 * column's phrases in RENDER order) whose stable ids overlap `entryElementIds`
 * (PLAT-10268 QA round 2).
 *
 * The overlap lookups above intentionally attach a cache/loading entry to
 * EVERY fragment overlapping the checked span — that is what makes a re-fix
 * indicator land at all (BUG B). But the spec wants exactly ONE badge per
 * analyzed span, at its END, so the caller renders the coverage slot only on
 * the phrase this returns `true` for: the last rendered phrase of the span.
 * Matched by object identity — the caller passes the same phrase objects it
 * renders.
 */
export function isLastOverlappingPhrase(
  entryElementIds: readonly string[],
  phrase: TranslationPhrase,
  renderedPhrases: readonly TranslationPhrase[],
): boolean {
  const covered = new Set(entryElementIds);
  const overlaps = (p: TranslationPhrase) =>
    p.originalElementIds?.some(id => covered.has(id)) ?? false;
  if (!overlaps(phrase)) return false;
  for (let i = renderedPhrases.length - 1; i >= 0; i--) {
    const p = renderedPhrases[i];
    if (overlaps(p)) return p === phrase;
  }
  // `phrase` overlaps but is not in the rendered list — no slot to pin.
  return false;
}
